/**
 * GpsVideoExportModal — GPS Replay Video Export with real map tiles
 * - 1920×1080 Full HD output
 * - Google Maps styled tiles (same dark-blue theme as real-time tracking)
 * - Mercator projection (consistent with tile coordinates)
 * - MediaRecorder (WebM/VP9, 8 Mbps)
 * - Custom time range selection (HH:MM → HH:MM)
 * - Dynamic video duration selector
 *
 * Props:
 *  - fetchPointsUrl : string
 *  - driverName     : string
 *  - subtitle       : string
 *  - filename       : string
 *  - onClose        : () => void
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { X, Download, Video, Loader2, CheckCircle2, AlertTriangle, Map as MapIcon, Clock } from 'lucide-react';
import config from 'config';

const API = config.API_URL;
const getToken = () => localStorage.getItem('access_token') || localStorage.getItem('token');

// ── Canvas constants — Full HD ─────────────────────────────────────────────
const CW = 1920;
const CH = 1080;
const PAD = 80;
const HEADER_H = 80;
const FOOTER_H = 44;
const FPS = 30;
const VIDEO_BITRATE = 8_000_000; // 8 Mbps for Full HD
const TILE_SIZE = 256;

const DURATION_OPTIONS = [
  { label: '15s', value: 15 },
  { label: '30s', value: 30 },
  { label: '45s', value: 45 },
  { label: '1min', value: 60 },
  { label: '1m30', value: 90 },
  { label: '2min', value: 120 },
];

// ── Map palette — identical to the real-time Google Maps dark-blue theme ──
const MAP_BG        = '#16162e'; // background (matches Google Maps #1a1a2e)
const MAP_HEADER_BG = 'rgba(22, 22, 46, 0.97)';
const MAP_OVERLAY   = 'rgba(10, 10, 30, 0.08)'; // subtle overlay on tiles

// Google Maps styled tiles — served through our backend proxy
// Renders the exact same dark-blue theme as the real-time tracking map.
// No CSS filter needed: tiles are pre-styled by Google Maps API.
const TILE_FILTER = 'none';
const tileUrl = (z, x, y) => `${API}/api/maps/tile/${z}/${x}/${y}.png`;

// ── Mercator helpers ──────────────────────────────────────────────────────
function mercPixX(lng, z) {
  return ((lng + 180) / 360) * Math.pow(2, z) * TILE_SIZE;
}
function mercPixY(lat, z) {
  const lr = (lat * Math.PI) / 180;
  return ((1 - Math.log(Math.tan(lr) + 1 / Math.cos(lr)) / Math.PI) / 2) * Math.pow(2, z) * TILE_SIZE;
}
function tileXY(lng, lat, z) {
  const lr = (lat * Math.PI) / 180;
  return {
    tx: Math.floor((lng + 180) / 360 * Math.pow(2, z)),
    ty: Math.floor((1 - Math.log(Math.tan(lr) + 1 / Math.cos(lr)) / Math.PI) / 2 * Math.pow(2, z)),
  };
}

function calcOptimalZoom(minLat, maxLat, minLng, maxLng, mapW, mapH) {
  const pad = 0.3;
  const latPad = (maxLat - minLat) * pad || 0.008;
  const lngPad = (maxLng - minLng) * pad || 0.008;
  for (let z = 16; z >= 5; z--) {
    const wx0 = mercPixX(minLng - lngPad, z);
    const wx1 = mercPixX(maxLng + lngPad, z);
    const wy0 = mercPixY(maxLat + latPad, z);
    const wy1 = mercPixY(minLat - latPad, z);
    if ((wx1 - wx0) <= mapW && (wy1 - wy0) <= mapH) return z;
  }
  return 8;
}

/**
 * Builds a Mercator-based transform: GPS (lat,lng) → canvas (x,y)
 * Returns { toCanvas, wx0, wy0, scale, drawX, drawY }
 */
function buildMapTransform(pts, z) {
  if (!pts.length) return null;
  const mapX0 = PAD;
  const mapY0 = HEADER_H + PAD;
  const mapW = CW - PAD * 2;
  const mapH = CH - HEADER_H - FOOTER_H - PAD * 2;

  let minLat = pts[0].lat, maxLat = pts[0].lat;
  let minLng = pts[0].lng, maxLng = pts[0].lng;
  for (const p of pts) {
    if (p.lat < minLat) minLat = p.lat;
    if (p.lat > maxLat) maxLat = p.lat;
    if (p.lng < minLng) minLng = p.lng;
    if (p.lng > maxLng) maxLng = p.lng;
  }
  const pad = 0.25;
  const latPad = (maxLat - minLat) * pad || 0.008;
  const lngPad = (maxLng - minLng) * pad || 0.008;

  const wx0 = mercPixX(minLng - lngPad, z);
  const wx1 = mercPixX(maxLng + lngPad, z);
  const wy0 = mercPixY(maxLat + latPad, z); // top in Mercator (maxLat = smaller Y)
  const wy1 = mercPixY(minLat - latPad, z); // bottom

  const worldW = wx1 - wx0;
  const worldH = wy1 - wy0;
  const scale = Math.min(mapW / worldW, mapH / worldH);
  const drawW = worldW * scale;
  const drawH = worldH * scale;
  const drawX = mapX0 + (mapW - drawW) / 2;
  const drawY = mapY0 + (mapH - drawH) / 2;

  const toCanvas = (lat, lng) => ({
    x: drawX + (mercPixX(lng, z) - wx0) * scale,
    y: drawY + (mercPixY(lat, z) - wy0) * scale,
  });

  return { toCanvas, wx0, wy0, scale, drawX, drawY, drawW, drawH };
}

/**
 * Returns list of tile {z,tx,ty,key} needed to cover the route bounding box.
 */
function getRequiredTiles(pts, z) {
  if (!pts.length) return [];
  let minLat = pts[0].lat, maxLat = pts[0].lat;
  let minLng = pts[0].lng, maxLng = pts[0].lng;
  for (const p of pts) {
    minLat = Math.min(minLat, p.lat);
    maxLat = Math.max(maxLat, p.lat);
    minLng = Math.min(minLng, p.lng);
    maxLng = Math.max(maxLng, p.lng);
  }
  const pad = 0.3;
  const latPad = (maxLat - minLat) * pad || 0.01;
  const lngPad = (maxLng - minLng) * pad || 0.01;

  const { tx: tx0, ty: ty0 } = tileXY(minLng - lngPad, maxLat + latPad, z);
  const { tx: tx1, ty: ty1 } = tileXY(maxLng + lngPad, minLat - latPad, z);
  const maxTile = Math.pow(2, z) - 1;

  const tiles = [];
  for (let tx = Math.max(0, tx0); tx <= Math.min(maxTile, tx1); tx++) {
    for (let ty = Math.max(0, ty0); ty <= Math.min(maxTile, ty1); ty++) {
      tiles.push({ z, tx, ty, key: `${z}/${tx}/${ty}` });
    }
  }
  return tiles;
}

// ── Canvas helpers ────────────────────────────────────────────────────────
function drawRoundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function fmtTime(ts) {
  return new Date(ts).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

// Convert HH:MM string to timestamp, using the date from referenceDate
function parseHHMMtoMs(hhMm, referenceDate) {
  const [hh, mm] = hhMm.split(':').map(Number);
  const d = new Date(referenceDate);
  d.setHours(hh, mm, 0, 0);
  return d.getTime();
}

function tsToHHMM(ts) {
  const d = new Date(ts);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function fmtDuration(minutes) {
  if (minutes < 60) return `${minutes}min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h${String(m).padStart(2, '0')}` : `${h}h`;
}

// ── Main component ────────────────────────────────────────────────────────
export default function GpsVideoExportModal({ fetchPointsUrl, driverName, subtitle, filename, onClose }) {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const logoImgRef = useRef(null);
  const tileImagesRef = useRef(new Map()); // key → HTMLImageElement
  const [speedSegments, setSpeedSegments] = useState([]); // Phase 2 — speed-coloured polyline

  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tilesLoading, setTilesLoading] = useState(false);
  const [tilesReady, setTilesReady] = useState(false);
  const [tileCount, setTileCount] = useState(0);
  const [tileLoaded, setTileLoaded] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(10);
  const [recording, setRecording] = useState(false);
  const [done, setDone] = useState(false);
  const [progress, setProgress] = useState(0);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoMime, setVideoMime] = useState('video/webm');
  const [error, setError] = useState(null);
  const [elapsedS, setElapsedS] = useState(0);
  const [logoReady, setLogoReady] = useState(false);

  // ── Time range & duration state ────────────────────────────────────────
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [targetDuration, setTargetDuration] = useState(30);

  // ── Fetch company logo ────────────────────────────────────────────────
  useEffect(() => {
    const img = new Image();
    img.onload = () => { logoImgRef.current = img; setLogoReady(true); };
    img.onerror = () => {};
    img.src = `${API}/api/cms/branding/logo-proxy`;
  }, []);

  // ── Fetch GPS points ──────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(fetchPointsUrl, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        const data = await res.json();
        setPoints(data.points || data.locations || []);
        // Phase 2 — speed-coloured trace (green/orange/red vs road limit)
        try {
          const speedUrl = fetchPointsUrl.replace('/locations', '/speed-trace');
          const sr = await fetch(speedUrl, { headers: { Authorization: `Bearer ${getToken()}` } });
          const sd = await sr.json();
          setSpeedSegments(sd.segments || []);
        } catch {
          setSpeedSegments([]);
        }
      } catch {
        setError('Erreur lors du chargement des points GPS.');
      }
      setLoading(false);
    };
    load();
  }, [fetchPointsUrl]);

  // ── Initialize time range from GPS points ─────────────────────────────
  useEffect(() => {
    if (!points.length) return;
    setRangeStart(tsToHHMM(new Date(points[0].recorded_at).getTime()));
    setRangeEnd(tsToHHMM(new Date(points[points.length - 1].recorded_at).getTime()));
  }, [points]);

  // ── Computed: filtered points by time range ───────────────────────────
  const filteredPoints = useMemo(() => {
    if (!points.length || !rangeStart || !rangeEnd) return points;
    const refDate = new Date(points[0].recorded_at);
    let startMs = parseHHMMtoMs(rangeStart, refDate);
    let endMs = parseHHMMtoMs(rangeEnd, refDate);
    // Handle overnight shifts (end time < start time)
    if (endMs <= startMs) endMs += 86400000;
    return points.filter(p => {
      const t = new Date(p.recorded_at).getTime();
      return t >= startMs && t <= endMs;
    });
  }, [points, rangeStart, rangeEnd]);

  // ── Pre-load map tiles once points are available ──────────────────────
  useEffect(() => {
    if (!points.length) return;

    const mapW = CW - PAD * 2;
    const mapH = CH - HEADER_H - FOOTER_H - PAD * 2;
    let minLat = points[0].lat, maxLat = points[0].lat;
    let minLng = points[0].lng, maxLng = points[0].lng;
    for (const p of points) {
      minLat = Math.min(minLat, p.lat);
      maxLat = Math.max(maxLat, p.lat);
      minLng = Math.min(minLng, p.lng);
      maxLng = Math.max(maxLng, p.lng);
    }

    const z = calcOptimalZoom(minLat, maxLat, minLng, maxLng, mapW, mapH);
    setZoomLevel(z);

    const tiles = getRequiredTiles(points, z);
    setTileCount(tiles.length);
    setTilesLoading(true);
    tileImagesRef.current = new Map();

    let loaded = 0;
    const onTileLoad = () => {
      loaded++;
      setTileLoaded(loaded);
      if (loaded >= tiles.length) {
        setTilesLoading(false);
        setTilesReady(true);
      }
    };

    if (!tiles.length) { setTilesLoading(false); setTilesReady(true); return; }

    for (const { z: tz, tx, ty, key } of tiles) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => { tileImagesRef.current.set(key, img); onTileLoad(); };
      img.onerror = () => onTileLoad(); // skip failed tiles gracefully
      img.src = tileUrl(tz, tx, ty);
    }
  }, [points]);

  // ── Draw map tiles on canvas ─────────────────────────────────────────
  // Tiles from our backend proxy are already styled (Google Maps dark theme).
  // No CSS filter needed — draw directly.
  const drawTiles = useCallback((ctx, z, transform) => {
    if (!transform) return;
    const { wx0, wy0, scale, drawX, drawY } = transform;
    ctx.save();
    tileImagesRef.current.forEach((img, key) => {
      const parts = key.split('/');
      if (parseInt(parts[0]) !== z) return;
      const tx = parseInt(parts[1]);
      const ty = parseInt(parts[2]);
      const canvasX = drawX + (tx * TILE_SIZE - wx0) * scale;
      const canvasY = drawY + (ty * TILE_SIZE - wy0) * scale;
      const tileSize = TILE_SIZE * scale;
      try { ctx.drawImage(img, canvasX, canvasY, tileSize, tileSize); } catch { /* skip */ }
    });
    ctx.restore();
  }, []);

  // ── Draw a single frame ───────────────────────────────────────────────
  const drawFrame = useCallback((ctx, pts, idx, transform, currentMs, progressVal) => {
    const z = zoomLevel;
    const timeStr = fmtTime(currentMs);
    const cur = pts[Math.min(idx, pts.length - 1)];
    const speedKmh = Math.round(cur?.speed || 0);

    // ── Background: solid dark navy base + map tiles ────────────────────────
    ctx.fillStyle = MAP_BG;
    ctx.fillRect(0, 0, CW, CH);

    // Map tiles (real Google Maps background — dark blue theme)
    drawTiles(ctx, z, transform);

    // Very subtle overlay to improve route/label readability on tiles
    if (transform) {
      const { drawX, drawY, drawW, drawH } = transform;
      ctx.fillStyle = MAP_OVERLAY;
      ctx.fillRect(drawX, drawY, drawW, drawH);
    }

    // ── Header ──────────────────────────────────────────────────────────
    ctx.fillStyle = MAP_HEADER_BG;
    ctx.fillRect(0, 0, CW, HEADER_H);
    ctx.strokeStyle = 'rgba(59,130,246,0.30)';
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(0, HEADER_H); ctx.lineTo(CW, HEADER_H); ctx.stroke();

    // Logo
    if (logoImgRef.current) {
      const lh = 42;
      const lw = Math.round(lh * (logoImgRef.current.width / logoImgRef.current.height));
      try {
        ctx.save(); ctx.globalAlpha = 0.92;
        ctx.drawImage(logoImgRef.current, 24, (HEADER_H - lh) / 2, lw, lh);
        ctx.globalAlpha = 1; ctx.restore();
      } catch { drawTextLogo(ctx); }
    } else {
      drawTextLogo(ctx);
    }

    // Driver name
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 26px "Courier New", monospace';
    ctx.textAlign = 'left';
    ctx.fillText(driverName || 'Chauffeur', 156, HEADER_H / 2 + 5);

    // Subtitle
    ctx.fillStyle = 'rgba(255,255,255,0.38)';
    ctx.font = '15px "Courier New", monospace';
    ctx.fillText(subtitle || '', 156, HEADER_H / 2 + 25);

    // Current time (right)
    ctx.textAlign = 'right';
    ctx.fillStyle = '#C9A24D';
    ctx.font = 'bold 34px "Courier New", monospace';
    ctx.fillText(timeStr, CW - 28, HEADER_H / 2 + 12);

    // Speed badge
    if (speedKmh > 0) {
      ctx.fillStyle = 'rgba(59,130,246,0.18)';
      drawRoundRect(ctx, CW - 152, HEADER_H / 2 + 16, 120, 28, 6); ctx.fill();
      ctx.fillStyle = '#60A5FA';
      ctx.font = '15px "Courier New", monospace';
      ctx.fillText(`${speedKmh} km/h`, CW - 34, HEADER_H / 2 + 34);
    }
    ctx.textAlign = 'left';

    // ── Route ────────────────────────────────────────────────────────────
    if (pts.length > 1 && transform) {
      const { toCanvas } = transform;
      const s0 = toCanvas(pts[0].lat, pts[0].lng);

      // Ghost path → speed-coloured route (green/orange/red vs road speed limit)
      const SPEED_COLORS = { green: '#10B981', orange: '#F59E0B', red: '#EF4444' };
      const segs = speedSegments;
      if (segs && segs.length) {
        for (const seg of segs) {
          if (!seg.path || seg.path.length < 2) continue;
          ctx.beginPath();
          ctx.strokeStyle = SPEED_COLORS[seg.color] || '#3B82F6';
          ctx.lineWidth = 4;
          ctx.lineJoin = 'round'; ctx.lineCap = 'round';
          const c0 = toCanvas(seg.path[0][0], seg.path[0][1]);
          ctx.moveTo(c0.x, c0.y);
          for (let i = 1; i < seg.path.length; i++) {
            const pt = toCanvas(seg.path[i][0], seg.path[i][1]);
            ctx.lineTo(pt.x, pt.y);
          }
          ctx.stroke();
        }
      } else {
        // Fallback: faint full path
        ctx.beginPath();
        ctx.strokeStyle = 'rgba(30,58,138,0.45)';
        ctx.lineWidth = 3;
        ctx.lineJoin = 'round';
        ctx.moveTo(s0.x, s0.y);
        for (const p of pts) { const pt = toCanvas(p.lat, p.lng); ctx.lineTo(pt.x, pt.y); }
        ctx.stroke();
      }

      // Completed segment — subtle white progress overlay (keeps speed colours visible)
      if (idx > 0) {
        ctx.beginPath();
        ctx.strokeStyle = 'rgba(255,255,255,0.35)';
        ctx.lineWidth = 2.5;
        ctx.lineJoin = 'round'; ctx.lineCap = 'round';
        ctx.moveTo(s0.x, s0.y);
        for (let i = 1; i <= Math.min(idx, pts.length - 1); i++) {
          const pt = toCanvas(pts[i].lat, pts[i].lng);
          ctx.lineTo(pt.x, pt.y);
        }
        ctx.stroke();
      }

      // Start marker
      ctx.beginPath(); ctx.arc(s0.x, s0.y, 11, 0, Math.PI * 2);
      ctx.fillStyle = '#10B981'; ctx.fill();
      ctx.strokeStyle = '#fff'; ctx.lineWidth = 3; ctx.stroke();
      ctx.fillStyle = '#fff'; ctx.font = 'bold 12px monospace'; ctx.textAlign = 'center';
      ctx.fillText('D', s0.x, s0.y + 4); ctx.textAlign = 'left';

      // End marker
      if (idx >= pts.length - 1) {
        const ep = toCanvas(pts[pts.length - 1].lat, pts[pts.length - 1].lng);
        ctx.beginPath(); ctx.arc(ep.x, ep.y, 11, 0, Math.PI * 2);
        ctx.fillStyle = '#EF4444'; ctx.fill();
        ctx.strokeStyle = '#fff'; ctx.lineWidth = 3; ctx.stroke();
        ctx.fillStyle = '#fff'; ctx.font = 'bold 12px monospace'; ctx.textAlign = 'center';
        ctx.fillText('A', ep.x, ep.y + 4); ctx.textAlign = 'left';
      }

      // Speed-colour legend (bottom-left of the map)
      if (speedSegments && speedSegments.length) {
        const lx = 24;
        const ly = CH - FOOTER_H - 96;
        ctx.fillStyle = 'rgba(22,22,46,0.9)';
        drawRoundRect(ctx, lx, ly, 184, 84, 8); ctx.fill();
        ctx.strokeStyle = 'rgba(201,162,77,0.3)'; ctx.lineWidth = 1;
        drawRoundRect(ctx, lx, ly, 184, 84, 8); ctx.stroke();
        const rows = [['#10B981', 'Vitesse respectée'], ['#F59E0B', 'Excès 1-10 km/h'], ['#EF4444', 'Excès > 10 km/h']];
        ctx.font = '13px sans-serif'; ctx.textAlign = 'left';
        rows.forEach(([col, lbl], i) => {
          const ry = ly + 20 + i * 22;
          ctx.strokeStyle = col; ctx.lineWidth = 4; ctx.lineCap = 'round';
          ctx.beginPath(); ctx.moveTo(lx + 14, ry); ctx.lineTo(lx + 40, ry); ctx.stroke();
          ctx.fillStyle = '#E5E5E5'; ctx.fillText(lbl, lx + 50, ry + 4);
        });
      }

      // Current position (golden arrow with pulse)
      if (cur) {
        const pt = toCanvas(cur.lat, cur.lng);
        ctx.beginPath(); ctx.arc(pt.x, pt.y, 28, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(201,162,77,0.18)'; ctx.lineWidth = 2; ctx.stroke();
        ctx.beginPath(); ctx.arc(pt.x, pt.y, 18, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(201,162,77,0.4)'; ctx.lineWidth = 2; ctx.stroke();

        // Heading: use device-reported heading if valid, otherwise compute
        // forward-looking bearing (cur → next) or fallback arrival bearing (prev → cur).
        let arrowHeading = (cur.heading && cur.heading !== 0) ? cur.heading : null;
        if (arrowHeading == null) {
          // Try forward-looking first
          for (let j = idx + 1; j < pts.length && j <= idx + 10; j++) {
            const dx = pts[j].lng - cur.lng;
            const dy = pts[j].lat - cur.lat;
            if (Math.abs(dx) > 0.00003 || Math.abs(dy) > 0.00003) {
              const dLng = (pts[j].lng - cur.lng) * Math.PI / 180;
              const y = Math.sin(dLng) * Math.cos(pts[j].lat * Math.PI / 180);
              const x = Math.cos(cur.lat * Math.PI / 180) * Math.sin(pts[j].lat * Math.PI / 180) -
                        Math.sin(cur.lat * Math.PI / 180) * Math.cos(pts[j].lat * Math.PI / 180) * Math.cos(dLng);
              arrowHeading = ((Math.atan2(y, x) * 180 / Math.PI) + 360) % 360;
              break;
            }
          }
        }
        if (arrowHeading == null && idx > 0) {
          // Fallback: arrival bearing
          for (let j = idx - 1; j >= 0 && j >= idx - 10; j--) {
            const dx = cur.lng - pts[j].lng;
            const dy = cur.lat - pts[j].lat;
            if (Math.abs(dx) > 0.00003 || Math.abs(dy) > 0.00003) {
              const dLng = (cur.lng - pts[j].lng) * Math.PI / 180;
              const y = Math.sin(dLng) * Math.cos(cur.lat * Math.PI / 180);
              const x = Math.cos(pts[j].lat * Math.PI / 180) * Math.sin(cur.lat * Math.PI / 180) -
                        Math.sin(pts[j].lat * Math.PI / 180) * Math.cos(cur.lat * Math.PI / 180) * Math.cos(dLng);
              arrowHeading = ((Math.atan2(y, x) * 180 / Math.PI) + 360) % 360;
              break;
            }
          }
        }

        ctx.save();
        ctx.translate(pt.x, pt.y);
        ctx.rotate(((arrowHeading || 0) * Math.PI) / 180);
        ctx.beginPath();
        ctx.moveTo(0, -16); ctx.lineTo(11, 11); ctx.lineTo(0, 5); ctx.lineTo(-11, 11);
        ctx.closePath();
        ctx.fillStyle = '#C9A24D'; ctx.fill();
        ctx.strokeStyle = '#fff'; ctx.lineWidth = 2; ctx.stroke();
        ctx.restore();

        // Speed tooltip
        if (speedKmh > 0) {
          ctx.fillStyle = 'rgba(22,22,46,0.92)';
          drawRoundRect(ctx, pt.x + 22, pt.y - 16, 96, 28, 5); ctx.fill();
          ctx.strokeStyle = 'rgba(201,162,77,0.3)'; ctx.lineWidth = 1;
          drawRoundRect(ctx, pt.x + 22, pt.y - 16, 96, 28, 5); ctx.stroke();
          ctx.fillStyle = '#C9A24D';
          ctx.font = 'bold 16px monospace';
          ctx.fillText(`${speedKmh} km/h`, pt.x + 28, pt.y + 5);
        }
      }
    }

    // ── Watermark ────────────────────────────────────────────────────────
    if (logoImgRef.current) {
      const wmH = 34;
      const wmW = Math.round(wmH * (logoImgRef.current.width / logoImgRef.current.height));
      const wmX = CW - wmW - 18;
      const wmY = CH - FOOTER_H - wmH - 10;
      ctx.fillStyle = 'rgba(22,22,46,0.65)';
      drawRoundRect(ctx, wmX - 8, wmY - 5, wmW + 16, wmH + 10, 7); ctx.fill();
      try {
        ctx.save(); ctx.globalAlpha = 0.8;
        ctx.drawImage(logoImgRef.current, wmX, wmY, wmW, wmH);
        ctx.globalAlpha = 1; ctx.restore();
      } catch { /* tainted */ }
    } else {
      ctx.fillStyle = 'rgba(201,162,77,0.4)';
      ctx.font = 'bold 18px monospace'; ctx.textAlign = 'right';
      ctx.fillText('Y GO', CW - 18, CH - FOOTER_H - 14); ctx.textAlign = 'left';
    }

    // ── Footer progress bar ──────────────────────────────────────────────
    ctx.fillStyle = MAP_HEADER_BG;
    ctx.fillRect(0, CH - FOOTER_H, CW, FOOTER_H);
    ctx.fillStyle = 'rgba(255,255,255,0.06)';
    drawRoundRect(ctx, 14, CH - FOOTER_H + 12, CW - 28, 10, 5); ctx.fill();
    if (progressVal > 0) {
      ctx.fillStyle = '#3B82F6'; // blue-500
      drawRoundRect(ctx, 14, CH - FOOTER_H + 12, Math.max(10, (CW - 28) * progressVal), 10, 5); ctx.fill();
    }
    ctx.fillStyle = 'rgba(255,255,255,0.22)';
    ctx.font = '12px monospace'; ctx.textAlign = 'left';
    ctx.fillText('Y GO — Replay GPS', 14, CH - 8);
    ctx.textAlign = 'right';
    ctx.fillText(`${Math.round(progressVal * 100)}%  ·  ${idx + 1}/${pts.length} pts`, CW - 14, CH - 8);
    ctx.textAlign = 'left';
  }, [driverName, subtitle, logoReady, drawTiles, zoomLevel, speedSegments]); // eslint-disable-line react-hooks/exhaustive-deps

  function drawTextLogo(ctx) {
    ctx.fillStyle = '#C9A24D'; ctx.font = 'bold 26px "Courier New", monospace';
    ctx.textAlign = 'left'; ctx.fillText('Y GO', 24, HEADER_H / 2 + 9);
  }

  // ── Draw initial preview (reacts to filteredPoints changes) ──────────
  useEffect(() => {
    if (!canvasRef.current || !filteredPoints.length || !tilesReady) return;
    const ctx = canvasRef.current.getContext('2d');
    const transform = buildMapTransform(filteredPoints, zoomLevel);
    const firstMs = new Date(filteredPoints[0].recorded_at).getTime();
    drawFrame(ctx, filteredPoints, 0, transform, firstMs, 0);
  }, [filteredPoints, drawFrame, tilesReady, zoomLevel]);

  // ── Start recording ───────────────────────────────────────────────────
  const startRecording = useCallback(() => {
    if (!canvasRef.current || !filteredPoints.length) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const transform = buildMapTransform(filteredPoints, zoomLevel);
    if (!transform) return;

    chunksRef.current = [];
    const stream = canvas.captureStream(FPS);
    const mimeTypes = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'];
    const mimeType = mimeTypes.find((m) => MediaRecorder.isTypeSupported(m)) || 'video/webm';
    setVideoMime(mimeType);

    const recorder = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: VIDEO_BITRATE });
    mediaRecorderRef.current = recorder;

    recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mimeType });
      setVideoUrl(URL.createObjectURL(blob));
      setDone(true);
      setRecording(false);
    };

    recorder.start(200);
    setRecording(true);
    setDone(false);
    setProgress(0);
    setElapsedS(0);

    const timestamps = filteredPoints.map((p) => new Date(p.recorded_at).getTime());
    const firstMs = timestamps[0];
    const lastMs = timestamps[timestamps.length - 1];
    const totalGpsMs = lastMs - firstMs || 1;
    const gpsAdvancePerFrame = totalGpsMs / (targetDuration * FPS);

    let currentMs = firstMs;
    const startTs = performance.now();

    const findIdx = (ms) => {
      let lo = 0, hi = timestamps.length - 1;
      while (lo < hi) {
        const mid = (lo + hi + 1) >> 1;
        if (timestamps[mid] <= ms) lo = mid; else hi = mid - 1;
      }
      return lo;
    };

    const animate = () => {
      if (currentMs > lastMs) {
        drawFrame(ctx, filteredPoints, filteredPoints.length - 1, transform, lastMs, 1.0);
        setTimeout(() => { if (recorder.state === 'recording') recorder.stop(); }, 800);
        return;
      }
      const idx = findIdx(currentMs);
      const progressVal = Math.min(1, (currentMs - firstMs) / totalGpsMs);
      drawFrame(ctx, filteredPoints, idx, transform, currentMs, progressVal);
      setProgress(progressVal);
      setElapsedS(Math.round((performance.now() - startTs) / 1000));
      currentMs += gpsAdvancePerFrame;
      animRef.current = requestAnimationFrame(animate);
    };
    animRef.current = requestAnimationFrame(animate);
  }, [filteredPoints, drawFrame, zoomLevel, targetDuration]);

  // ── Cleanup ───────────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop();
    };
  }, []);

  const handleClose = () => {
    if (animRef.current) cancelAnimationFrame(animRef.current);
    if (mediaRecorderRef.current?.state === 'recording') mediaRecorderRef.current.stop();
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    onClose();
  };

  // Reset done/video when range or duration changes
  const handleRangeChange = (setter) => (e) => {
    if (recording) return;
    setter(e.target.value);
    setDone(false);
    setVideoUrl(null);
    setProgress(0);
  };

  const totalFirstMs = points.length ? new Date(points[0].recorded_at).getTime() : 0;
  const totalLastMs = points.length ? new Date(points[points.length - 1].recorded_at).getTime() : 0;
  const totalDurationMin = Math.round((totalLastMs - totalFirstMs) / 60000);

  const filtFirstMs = filteredPoints.length ? new Date(filteredPoints[0].recorded_at).getTime() : 0;
  const filtLastMs = filteredPoints.length ? new Date(filteredPoints[filteredPoints.length - 1].recorded_at).getTime() : 0;
  const filtDurationMin = Math.round((filtLastMs - filtFirstMs) / 60000);

  const ext = videoMime.includes('mp4') ? 'mp4' : 'webm';
  const dlFilename = `${filename || 'replay'}.${ext}`;
  const isReady = tilesReady && filteredPoints.length > 0;

  // Bounds for time inputs (HH:MM of first and last points)
  const minTime = points.length ? tsToHHMM(totalFirstMs) : '00:00';
  const maxTime = points.length ? tsToHHMM(totalLastMs) : '23:59';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-md p-4"
      data-testid="gps-video-export-modal">
      <div className="bg-[#12122a] border border-blue-900/30 rounded-2xl overflow-hidden w-full max-w-5xl shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-blue-900/20 bg-[#0e0e22]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
              <Video className="w-4 h-4 text-blue-400" />
            </div>
            <div>
              <h2 className="text-white font-semibold text-sm">Export Vidéo — Replay GPS</h2>
              <p className="text-white/35 text-xs mt-0.5">
                {driverName} · {subtitle}
                {totalDurationMin > 0 && ` · Shift total : ${fmtDuration(totalDurationMin)}`}
              </p>
            </div>
          </div>
          <button onClick={handleClose} disabled={recording}
            className="p-2 rounded-lg text-white/40 hover:text-white hover:bg-white/10 disabled:opacity-30 transition"
            data-testid="gps-video-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-56 gap-3">
              <Loader2 className="w-8 h-8 text-[#C9A24D] animate-spin" />
              <p className="text-white/40 text-sm">Chargement des points GPS…</p>
            </div>
          ) : error ? (
            <div className="flex items-center gap-3 justify-center h-40 text-red-400 text-sm">
              <AlertTriangle className="w-5 h-5" />{error}
            </div>
          ) : points.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-44 gap-3 text-center">
              <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center">
                <Video className="w-5 h-5 text-white/20" />
              </div>
              <p className="text-white/40 text-sm">Aucun point GPS disponible.</p>
            </div>
          ) : (
            <>
              {/* ── Time Range & Duration Selector ─────────────────────── */}
              <div className="mb-4 px-4 py-3 bg-blue-950/30 border border-blue-900/25 rounded-xl"
                data-testid="gps-time-range-panel">
                <div className="flex flex-wrap items-center gap-4">
                  {/* Time range */}
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <Clock className="w-3.5 h-3.5 text-[#C9A24D] shrink-0" />
                    <span className="text-white/50 text-xs shrink-0">Plage :</span>
                    <input
                      type="time"
                      value={rangeStart}
                      min={minTime}
                      max={maxTime}
                      onChange={handleRangeChange(setRangeStart)}
                      disabled={recording}
                      className="bg-[#1a1a3a] border border-blue-900/40 rounded-lg px-2.5 py-1 text-white text-xs font-mono
                                 focus:outline-none focus:border-blue-500/60 disabled:opacity-40 transition
                                 [color-scheme:dark]"
                      data-testid="gps-range-start-input"
                    />
                    <span className="text-white/30 text-xs">→</span>
                    <input
                      type="time"
                      value={rangeEnd}
                      min={minTime}
                      max={maxTime}
                      onChange={handleRangeChange(setRangeEnd)}
                      disabled={recording}
                      className="bg-[#1a1a3a] border border-blue-900/40 rounded-lg px-2.5 py-1 text-white text-xs font-mono
                                 focus:outline-none focus:border-blue-500/60 disabled:opacity-40 transition
                                 [color-scheme:dark]"
                      data-testid="gps-range-end-input"
                    />
                    {filteredPoints.length > 0 && (
                      <span className="text-white/30 text-xs ml-1 shrink-0">
                        {filteredPoints.length} pts · {fmtDuration(filtDurationMin)}
                      </span>
                    )}
                    {filteredPoints.length === 0 && rangeStart && rangeEnd && (
                      <span className="text-red-400/70 text-xs ml-1 shrink-0">Aucun point dans cette plage</span>
                    )}
                  </div>

                  {/* Duration selector */}
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-white/50 text-xs">Durée vidéo :</span>
                    <div className="flex gap-1" data-testid="gps-duration-selector">
                      {DURATION_OPTIONS.map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => { if (!recording) { setTargetDuration(opt.value); setDone(false); setVideoUrl(null); } }}
                          disabled={recording}
                          className={`px-2.5 py-1 rounded-lg text-xs font-mono transition
                            ${targetDuration === opt.value
                              ? 'bg-blue-600 text-white font-bold'
                              : 'bg-blue-950/40 text-white/40 hover:bg-blue-900/40 hover:text-white/70'
                            } disabled:opacity-40`}
                          data-testid={`gps-duration-btn-${opt.value}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Tile loading state */}
              {tilesLoading && (
                <div className="mb-3 flex items-center gap-3 px-4 py-3 bg-blue-500/8 border border-blue-500/20 rounded-xl">
                  <MapIcon className="w-4 h-4 text-blue-400 animate-pulse shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-blue-400 text-xs font-medium">Chargement de la carte…</p>
                    <div className="mt-1.5 h-1 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-400/60 rounded-full transition-all duration-200"
                        style={{ width: `${tileCount ? Math.round((tileLoaded / tileCount) * 100) : 0}%` }} />
                    </div>
                  </div>
                  <span className="text-blue-400/60 text-[11px] font-mono shrink-0">{tileLoaded}/{tileCount}</span>
                </div>
              )}

              <div className="relative rounded-xl overflow-hidden border border-white/10 bg-[#080810]">
                <canvas ref={canvasRef} width={CW} height={CH}
                  className="w-full" style={{ aspectRatio: '16/9', display: 'block' }}
                  data-testid="gps-replay-canvas" />

                {tilesLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                    <div className="flex flex-col items-center gap-2">
                      <MapIcon className="w-8 h-8 text-blue-400 animate-pulse" />
                      <p className="text-white/60 text-sm">Chargement des tuiles de carte…</p>
                    </div>
                  </div>
                )}
                {recording && (
                  <div className="absolute top-3 right-3 flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/20 border border-red-500/40 backdrop-blur-sm"
                    data-testid="gps-recording-badge">
                    <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                    <span className="text-red-400 text-[11px] font-semibold tracking-wider">REC</span>
                    <span className="text-red-400/60 text-[11px]">{elapsedS}s / {targetDuration}s</span>
                  </div>
                )}
                {done && (
                  <div className="absolute top-3 right-3 flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/20 border border-emerald-500/40">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                    <span className="text-emerald-400 text-[11px] font-semibold">PRÊT</span>
                  </div>
                )}
              </div>

              {recording && (
                <div className="mt-3 flex items-center gap-3" data-testid="gps-recording-progress">
                  <div className="flex-1 h-1.5 bg-blue-950/40 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 rounded-full transition-all duration-150"
                      style={{ width: `${Math.round(progress * 100)}%` }} />
                  </div>
                  <span className="text-blue-400 text-xs font-mono min-w-[36px] text-right">
                    {Math.round(progress * 100)}%
                  </span>
                </div>
              )}

              {done && videoUrl && (
                <div className="mt-4 flex items-center gap-4 px-5 py-4 bg-emerald-500/8 border border-emerald-500/20 rounded-xl"
                  data-testid="gps-download-ready-banner">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-emerald-400 text-sm font-semibold">Vidéo prête !</p>
                    <p className="text-white/35 text-xs mt-0.5 truncate">{dlFilename}</p>
                  </div>
                  <a href={videoUrl} download={dlFilename}
                    className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-bold hover:bg-blue-500 transition shrink-0"
                    data-testid="gps-download-video-btn">
                    <Download className="w-4 h-4" />Télécharger
                  </a>
                </div>
              )}

              <div className="mt-3 flex items-center gap-2.5 text-[11px] text-white/20 flex-wrap">
                <span>{filteredPoints.length} pts sélectionnés</span>
                {totalDurationMin !== filtDurationMin && (
                  <><span>·</span><span>{fmtDuration(filtDurationMin)} de données</span></>
                )}
                <span>·</span><span>1920×1080 Full HD</span>
                <span>·</span><span>WebM/VP9 · 8 Mbps</span>
                <span>·</span><span>~{targetDuration}s de vidéo</span>
                <span>·</span>
                <span className={tilesReady ? 'text-emerald-400/70' : tilesLoading ? 'text-blue-400/70' : 'text-white/20'}>
                  {tilesReady ? `✓ Carte bleue (z${zoomLevel})` : tilesLoading ? `Carte en cours… (${tileLoaded}/${tileCount})` : 'Carte en attente'}
                </span>
                <span>·</span>
                <span className={logoReady ? 'text-emerald-400/60' : 'text-white/20'}>
                  {logoReady ? '✓ Logo' : 'Logo…'}
                </span>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-blue-900/20 flex items-center justify-between gap-3">
          <p className="text-white/20 text-xs">
            Généré localement · Tuiles CartoDB (thème bleu) · Aucune donnée envoyée au serveur.
          </p>
          <div className="flex items-center gap-2 shrink-0">
            {done && (
              <button onClick={() => { setDone(false); setProgress(0); setVideoUrl(null); setElapsedS(0); }}
                className="px-4 py-2 text-white/50 bg-white/5 rounded-xl text-sm font-medium hover:bg-white/10 transition"
                data-testid="gps-regenerate-btn">
                Régénérer
              </button>
            )}
            {!done && !recording && isReady && (
              <button onClick={startRecording}
                className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-bold hover:bg-blue-500 transition"
                data-testid="gps-start-recording-btn">
                <Video className="w-4 h-4" />Générer la vidéo
              </button>
            )}
            {!done && !recording && tilesLoading && (
              <button disabled
                className="flex items-center gap-2 px-6 py-2.5 bg-blue-500/20 text-blue-400 rounded-xl text-sm font-bold cursor-not-allowed">
                <MapIcon className="w-4 h-4 animate-pulse" />Chargement carte…
              </button>
            )}
            {recording && (
              <button disabled
                className="flex items-center gap-2 px-6 py-2.5 bg-blue-600/20 text-blue-400 rounded-xl text-sm font-bold cursor-not-allowed">
                <Loader2 className="w-4 h-4 animate-spin" />Enregistrement…
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
